import { Api } from '../../../../../services/interceptor';
import { QUERY_KEY } from '../../../../../services/queryEndPoint';

export interface ITeammateWaiverStatus {
  playerId: string;
  playerName: string;
  playerRole: string | null;
  signed: boolean;
  self: boolean;
}

export interface IMyWaiverStatus {
  waiverRequired: boolean;
  waiverId: string | null;
  waiverName: string | null;
  signed: boolean;
  teamSnapshotId: string | null;
  teamName: string | null;
  teammates: ITeammateWaiverStatus[];
}

export type SignatureType = 'TYPED' | 'DRAWN';

export interface IWaiverSignatureRequest {
  agreedToTerms: boolean;
  /** Typed-name signature. Ignored when signatureType is 'DRAWN' — use digitalSignatureImage instead. */
  digitalSignature?: string;
  signatureType?: SignatureType;
  /** Drawn signature, as a base64 PNG data URL (canvas.toDataURL()). Only sent when signatureType is 'DRAWN'. */
  digitalSignatureImage?: string;
  dateConfirmed?: string; // yyyy-MM-dd
  signerIsMinor?: boolean;
  parentGuardianName?: string;
  fieldValues: Record<string, string>;
}

export interface IWaiverSignature {
  id: number;
  playerId: string;
  tournamentId: string;
  tournamentName: string | null;
  waiverId: string;
  waiverName: string | null;
  signedAt: string;
  agreedToTerms: boolean;
  /** The typed name (signatureType 'TYPED') or a raw S3 key (signatureType 'DRAWN' — prefer digitalSignatureUrl). */
  digitalSignature: string | null;
  signatureType: SignatureType | null;
  /** Ready-to-use image URL, set only when signatureType is 'DRAWN'. */
  digitalSignatureUrl: string | null;
  dateConfirmed: string | null;
  parentGuardianName: string | null;
  fieldValues: Record<string, string> | null;
}

/** Unwraps the backend's { success, message, data, errorCode } envelope. */
function unwrapData<T>(res: { data?: { data?: T } }): T {
  return res?.data?.data as T;
}

/** GET tournament/{tournamentId}/my-waiver-status — self only. */
export async function fetchMyWaiverStatus(tournamentId: string): Promise<IMyWaiverStatus> {
  const res = await Api.get(`${QUERY_KEY.tournamentWaiverSignature}/${tournamentId}/my-waiver-status`);
  return unwrapData<IMyWaiverStatus>(res);
}

export interface IWaiverSignatureStatus {
  signed: boolean;
  signature: IWaiverSignature | null;
}

/** GET tournament/{tournamentId}/waiver-signature/{playerId} — the player about themselves, or a league admin about anyone. */
export async function fetchWaiverSignatureStatus(
  tournamentId: string,
  playerId: string
): Promise<IWaiverSignatureStatus> {
  const res = await Api.get(`${QUERY_KEY.tournamentWaiverSignature}/${tournamentId}/waiver-signature/${playerId}`);
  return unwrapData<IWaiverSignatureStatus>(res);
}

/** POST tournament/{tournamentId}/waiver-signature — self sign. */
export async function signTournamentWaiver(
  tournamentId: string,
  request: IWaiverSignatureRequest
): Promise<IWaiverSignature> {
  const res = await Api.post(
    `${QUERY_KEY.tournamentWaiverSignature}/${tournamentId}/waiver-signature`,
    request
  );
  return unwrapData<IWaiverSignature>(res);
}

export interface IEmailDispatchResult {
  sent: boolean;
  recipient: string | null;
  failureReason: string | null;
}

/** GET tournament/{tournamentId}/waiver-signature — every signature captured. League-admin only. */
export async function fetchTournamentWaiverSignatures(tournamentId: string): Promise<IWaiverSignature[]> {
  const res = await Api.get(`${QUERY_KEY.tournamentWaiverSignature}/${tournamentId}/waiver-signature`);
  return unwrapData<IWaiverSignature[]>(res) ?? [];
}

/** POST tournament/{tournamentId}/waiver-signature/{playerId}/remind — league-admin only. */
export async function remindPlayerToSignWaiver(
  tournamentId: string,
  playerId: string
): Promise<IEmailDispatchResult> {
  const res = await Api.post(
    `${QUERY_KEY.tournamentWaiverSignature}/${tournamentId}/waiver-signature/${playerId}/remind`
  );
  return unwrapData<IEmailDispatchResult>(res);
}

/** POST tournament/{tournamentId}/waiver-signature/remind — reminds every unsigned roster player. League-admin only. */
export async function remindAllUnsignedPlayers(tournamentId: string): Promise<IEmailDispatchResult[]> {
  const res = await Api.post(`${QUERY_KEY.tournamentWaiverSignature}/${tournamentId}/waiver-signature/remind`);
  return unwrapData<IEmailDispatchResult[]>(res) ?? [];
}
